import {
  Body1,
  Button,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Dialog as FluentDialog,
  Spinner,
  ToolbarDivider,
  makeStyles,
  mergeClasses,
  tokens,
  useId,
} from '@fluentui/react-components'
import { Dismiss24Regular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'

interface DialogProps {
  title: string
  content: string | React.ReactNode
  customDialogTitleActions?: React.ReactNode
  primaryButtonText?: string
  secondaryButtonText?: string
  onConfirm?: () => void
  onOpenChange: (open: boolean) => void
  isDialogOpen: boolean
  isDismissible?: boolean
  isLoading?: boolean
  loadingLabel?: string
  keepSecondaryActionVisibleWhileLoading?: boolean
  isError?: boolean
  errorMessage?: string
  /** Merged onto DialogSurface — use sparingly for one-off layout overrides (e.g. extra-wide dialogs). */
  surfaceClassName?: string
}
const useStyles = makeStyles({
  dialog: {
    margin: `auto ${tokens.spacingHorizontalM}`,
    '@media (min-width: 600px)': {
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  dialogMaxWidth: {
    '@media (min-width: 600px)': {
      maxWidth: '32em',
    },
  },
  dialogContentHidden: {
    visibility: 'hidden',
  },
  loadingWrapper: {
    position: 'relative',
  },
  loadingOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexDirection: 'row',
  },
})
const Dialog = ({
  title,
  customDialogTitleActions,
  content,
  primaryButtonText,
  secondaryButtonText,
  onConfirm,
  onOpenChange,
  isDialogOpen,
  isDismissible = true,
  isLoading = false,
  loadingLabel,
  keepSecondaryActionVisibleWhileLoading = false,
  isError = false,
  errorMessage,
  surfaceClassName,
}: DialogProps) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const contentId = useId('dialog-content')
  const shouldHideDialogContent = isLoading || isError
  const hasSecondaryAction =
    typeof secondaryButtonText === 'string' && secondaryButtonText.length > 0
  const shouldShowSecondaryAction =
    hasSecondaryAction && (!isLoading || keepSecondaryActionVisibleWhileLoading)
  const shouldHideDialogActions = isLoading && !shouldShowSecondaryAction

  return (
    <FluentDialog
      open={isDialogOpen}
      // Forward the explicit next open state to avoid toggle races during portal/focus handoff.
      onOpenChange={(_, data) => {
        if (!isDismissible && !data.open) {
          return
        }

        onOpenChange(data.open)
      }}
      modalType="modal">
      <DialogSurface
        className={mergeClasses(
          classes.dialog,
          !surfaceClassName && classes.dialogMaxWidth,
          surfaceClassName
        )}
        aria-describedby={contentId}>
        <div className={classes.loadingWrapper}>
          {/* Keep title/close visible; hide content/actions while loading to preserve size and avoid overlap. */}
          <DialogBody>
            <DialogTitle
              action={
                <div className={classes.actions}>
                  {customDialogTitleActions}
                  {!secondaryButtonText && isDismissible ? (
                    <>
                      {customDialogTitleActions ? <ToolbarDivider /> : null}
                      <DialogTrigger action="close">
                        <Button
                          appearance="subtle"
                          aria-label={t('close')}
                          icon={<Dismiss24Regular />}
                        />
                      </DialogTrigger>
                    </>
                  ) : null}
                </div>
              }>
              {title}
            </DialogTitle>
            <DialogContent
              id={contentId}
              className={shouldHideDialogContent ? classes.dialogContentHidden : undefined}>
              {content}
            </DialogContent>
            <DialogActions
              className={shouldHideDialogActions ? classes.dialogContentHidden : undefined}>
              {primaryButtonText && onConfirm && (
                <Button appearance="primary" onClick={onConfirm} disabled={isLoading || isError}>
                  {primaryButtonText}
                </Button>
              )}
              {shouldShowSecondaryAction && secondaryButtonText && (
                <DialogTrigger action="close">
                  <Button appearance="secondary">{secondaryButtonText}</Button>
                </DialogTrigger>
              )}
            </DialogActions>
          </DialogBody>
          {isLoading && (
            <div className={classes.loadingOverlay}>
              <Spinner
                label={loadingLabel ? loadingLabel : t('loading')}
                labelPosition="below"
                size="extra-tiny"
              />
            </div>
          )}
          {isError && (
            <div className={classes.loadingOverlay}>
              <Body1 as="p">{errorMessage ?? t('genericError')}</Body1>
            </div>
          )}
        </div>
      </DialogSurface>
    </FluentDialog>
  )
}

export { Dialog }
